/**
 * Common helpers for the IAM v2 courses admin-preview session, storing the admin token as a
 * base64-JSON envelope under the `acatv2` key. Mirrors membership-hmi-preview PR #1321
 * (`permission.helper.ts` `acatv2` handling + `http-common.ts` `hasAdminV2PreviewSession`).
 *
 * Uses `sessionStorage`, not a cookie (PR #1321 `fix: move to sessionStorage`): the ~5.7KB envelope
 * exceeds the ~4KB per-cookie limit (silent drop), and `sessionStorage` gives this high-privilege
 * token a smaller blast radius than `localStorage` by scoping it to the tab and clearing on close.
 *
 * Client-only: on SSR these reads return null and requests fall back to the learner path.
 *
 * Lives in the COMMON core-layer, not the courses admin-preview layer, so the shared `BaseService`
 * can read the admin session without importing a courses layer.
 */

/** Dedicated sessionStorage key for the IAM v2 admin-preview token. */
export const ADMIN_PREVIEW_STORAGE_KEY = 'acatv2' as const

/** The `role` value minted by the `/user/v2/login-token` response that marks an admin preview. */
const ADMIN_PREVIEW_ROLE = 'admin' as const

/**
 * Base64-JSON envelope persisted under the `acatv2` key. `token` is the canonical field
 * (clientportal-core style); `accessToken` is the raw membership-backend response key. Prefer
 * `token`, fall back to `accessToken`.
 */
export interface AdminV2PreviewEnvelope {
  token?: string
  accessToken?: string
  role?: string
  locationId?: string
  tokenExpirationTime?: number | string
}

/** Decode a base64-JSON `acatv2` envelope. Returns null for anything that is not our envelope. */
export function parseAdminV2PreviewEnvelope(raw: string | null | undefined): AdminV2PreviewEnvelope | null {
  if (!raw) return null
  try {
    const decoded = typeof atob !== 'undefined' ? atob(raw) : Buffer.from(raw, 'base64').toString('utf-8')
    const parsed = JSON.parse(decoded) as unknown
    if (parsed && typeof parsed === 'object') {
      return parsed as AdminV2PreviewEnvelope
    }
    return null
  } catch {
    // Parse failure just means "not a v2 admin preview" — expected and non-fatal.
    return null
  }
}

/** True when the envelope carries an expiry that is at or before now. Missing expiry = not expired. */
export function isAdminV2PreviewEnvelopeExpired(envelope: AdminV2PreviewEnvelope | null): boolean {
  if (!envelope?.tokenExpirationTime) return false
  const expiresAt = parseInt(String(envelope.tokenExpirationTime), 10)
  if (Number.isNaN(expiresAt)) return false
  return expiresAt <= Date.now()
}

/** A valid admin-preview session requires role `admin`, a token, and a non-expired envelope. */
export function isValidAdminV2PreviewEnvelope(envelope: AdminV2PreviewEnvelope | null): boolean {
  if (!envelope) return false
  if (envelope.role !== ADMIN_PREVIEW_ROLE) return false
  if (!(envelope.token || envelope.accessToken)) return false
  return !isAdminV2PreviewEnvelopeExpired(envelope)
}

/** The IAM v2 admin access token when the envelope is a valid admin-preview session, else null. */
export function getAdminV2PreviewAccessToken(envelope: AdminV2PreviewEnvelope | null): string | null {
  if (!isValidAdminV2PreviewEnvelope(envelope)) return null
  return envelope?.token || envelope?.accessToken || null
}

/** The server-minted location id when the envelope is a valid admin-preview session, else null. */
export function getAdminV2PreviewLocationId(envelope: AdminV2PreviewEnvelope | null): string | null {
  if (!isValidAdminV2PreviewEnvelope(envelope)) return null
  return envelope?.locationId || null
}

/** Safe access to `sessionStorage` — returns null on SSR or when storage is unavailable. */
function getSessionStorage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null
    return window.sessionStorage
  } catch {
    return null
  }
}

/** Client-only: read + parse the `acatv2` envelope from sessionStorage. Returns null on SSR. */
export function readAdminV2PreviewEnvelope(): AdminV2PreviewEnvelope | null {
  const storage = getSessionStorage()
  if (!storage) return null
  return parseAdminV2PreviewEnvelope(storage.getItem(ADMIN_PREVIEW_STORAGE_KEY))
}

/** Client-only: persist the `acatv2` envelope (base64-JSON) to sessionStorage. No-op on SSR. */
export function writeAdminV2PreviewEnvelope(envelope: AdminV2PreviewEnvelope): void {
  const storage = getSessionStorage()
  if (!storage) return
  storage.setItem(ADMIN_PREVIEW_STORAGE_KEY, btoa(JSON.stringify(envelope)))
}

/** Client-only: clear the `acatv2` session (called when leaving the preview). No-op on SSR. */
export function clearAdminV2PreviewEnvelope(): void {
  const storage = getSessionStorage()
  if (!storage) return
  storage.removeItem(ADMIN_PREVIEW_STORAGE_KEY)
}

/**
 * Whether a valid admin-preview session exists. Defaults to reading the `acatv2` sessionStorage
 * entry, but callers with an already-resolved envelope may pass it.
 */
export function hasAdminV2PreviewSession(envelope: AdminV2PreviewEnvelope | null = readAdminV2PreviewEnvelope()): boolean {
  return isValidAdminV2PreviewEnvelope(envelope)
}

/** Dedicated sessionStorage key for the ephemeral `v2.` agency preview token. */
export const ADMIN_PREVIEW_AGENCY_TOKEN_STORAGE_KEY = 'acatv2-agency' as const

/**
 * Client-only persistence for the raw `v2.` agency preview token (not the already-exchanged
 * `acatv2` access token) — the federated `membership-hmi-preview` remote runs its own admin
 * sign-in exchange with it. On full reload the token is gone from the URL/in-memory store, so
 * without this the remote's exchange would 401; kept in `sessionStorage` (per-tab, cleared on tab
 * close and when leaving `/courses`) so it can be replayed within its TTL — the token is already
 * exchanged twice on fresh entry (host + remote), so this replay is expected. Never written to a
 * cookie, the SSR payload, or logs.
 */
export function readAdminPreviewAgencyToken(): string | null {
  const storage = getSessionStorage()
  if (!storage) return null
  return storage.getItem(ADMIN_PREVIEW_AGENCY_TOKEN_STORAGE_KEY) || null
}

/** Client-only: persist the `v2.` agency preview token to sessionStorage. No-op on SSR. */
export function writeAdminPreviewAgencyToken(token: string): void {
  const storage = getSessionStorage()
  if (!storage || !token) return
  storage.setItem(ADMIN_PREVIEW_AGENCY_TOKEN_STORAGE_KEY, token)
}

/** Client-only: clear the persisted `v2.` agency preview token. No-op on SSR. */
export function clearAdminPreviewAgencyToken(): void {
  const storage = getSessionStorage()
  if (!storage) return
  storage.removeItem(ADMIN_PREVIEW_AGENCY_TOKEN_STORAGE_KEY)
}
